import type { ThreatReportCreator } from '@/shared/cors-edge-lambda-api';
import type { CaseThreatReport } from './threat-report-sharing';
import { threatReportSourceStamp, type ThreatReportSourceStamp } from './threat-report-sources';

/** Selectors named in the header before the rest collapse into "+N more". */
export const SELECTOR_PREVIEW_COUNT = 3;

type SelectorName = Pick<ThreatReportCreator, 'creatorId' | 'creatorName' | 'creatorHandles'> &
  Partial<Pick<ThreatReportCreator, 'platform' | 'activityCount'>>;

/** The mailbox name of an analyst address. A value with no `@` is kept as written. */
export function threatReportCreatorUsername(createdBy: string | null | undefined): string {
  const trimmed = createdBy?.trim() ?? '';
  const at = trimmed.lastIndexOf('@');
  return at > 0 ? trimmed.slice(0, at) : trimmed;
}

/** Best label for a selector: its name, else its first handle, else its id. */
export function threatReportSelectorName(creator: SelectorName): string {
  return creator.creatorName.trim() || creator.creatorHandles[0]?.trim() || creator.creatorId;
}

export interface ThreatReportSelectorSummary {
  /** Selectors the report covers. Null while an unscoped report waits on its export. */
  count: number | null;
  /** Every selector label, in report order. */
  labels: string[];
  /** The first few selector labels, at most ``previewCount``. */
  names: string[];
  /** Selectors not listed in ``names``. */
  remaining: number;
  /** The report was not scoped, so it covers every account in its export. */
  allAccounts: boolean;
}

/**
 * Count and preview the selectors a report covers.
 *
 * A scoped report lists its own selector ids and resolves them to names from
 * the export's accounts. An id the export no longer lists keeps its id as the
 * label. An unscoped report covers the whole export, so it depends on
 * ``exportCreators`` for both count and names.
 */
export function summarizeThreatReportSelectors(
  creatorIds: readonly string[],
  exportCreators: readonly SelectorName[] | undefined,
  previewCount: number = SELECTOR_PREVIEW_COUNT,
): ThreatReportSelectorSummary {
  const ids = [...new Set(creatorIds.map((id) => id.trim()).filter(Boolean))];

  if (ids.length > 0) {
    const byId = new Map((exportCreators ?? []).map((creator) => [creator.creatorId, creator]));
    const labels = ids.map((id) => {
      const creator = byId.get(id);
      return creator ? threatReportSelectorName(creator) : id;
    });
    const names = labels.slice(0, previewCount);
    return { count: ids.length, labels, names, remaining: labels.length - names.length, allAccounts: false };
  }

  if (!exportCreators) return { count: null, labels: [], names: [], remaining: 0, allAccounts: true };
  const labels = exportCreators.map(threatReportSelectorName);
  const names = labels.slice(0, previewCount);
  return {
    count: exportCreators.length,
    labels,
    names,
    remaining: labels.length - names.length,
    allAccounts: true,
  };
}

/** One selector row for the details dialog: name plus the export's extra columns. */
export interface ThreatReportSelectorDetail {
  id: string;
  name: string;
  platform: string;
  handles: string[];
  activityCount: number | null;
}

/**
 * Every selector the report covers, with the export's platform, handles, and
 * activity count when that account is still in the export.
 */
export function threatReportSelectorDetails(
  creatorIds: readonly string[],
  exportCreators: readonly SelectorName[] | undefined,
): ThreatReportSelectorDetail[] {
  const ids = [...new Set(creatorIds.map((id) => id.trim()).filter(Boolean))];
  const listed = exportCreators ?? [];
  const byId = new Map(listed.map((creator) => [creator.creatorId, creator]));
  const source: readonly SelectorName[] =
    ids.length > 0
      ? ids.map((id) => byId.get(id) ?? { creatorId: id, creatorName: '', creatorHandles: [] })
      : listed;

  return source.map((creator) => ({
    id: creator.creatorId,
    name: threatReportSelectorName(creator),
    platform: creator.platform?.trim() ?? '',
    handles: (creator.creatorHandles ?? []).map((handle) => handle.trim()).filter(Boolean),
    activityCount: typeof creator.activityCount === 'number' ? creator.activityCount : null,
  }));
}

/** Which clock the Fivecast card is showing, so the label can name it. */
export type ThreatReportExportDateKind = 'filename-day' | 'filename-time' | 'uploaded' | 'unknown' | 'missing';

export function threatReportExportDateKind(
  report: Pick<CaseThreatReport, 'fivecastDataDirectory'>,
  uploadedAtByUrl: Record<string, string> = {},
): ThreatReportExportDateKind {
  if (!report.fivecastDataDirectory) return 'missing';
  const stamp = threatReportExportStamp(report, uploadedAtByUrl);
  if (!stamp) return 'unknown';
  if (stamp.origin === 'uploaded') return 'uploaded';
  return stamp.precision === 'day' ? 'filename-day' : 'filename-time';
}

/** When the analyst ran the report: its stored timestamp, else its epoch. */
export function threatReportCreatedAt(
  report: Pick<CaseThreatReport, 'timestamp' | 'epochTimeSecs'>,
): Date | null {
  const parsed = new Date(report.timestamp ?? report.epochTimeSecs * 1000);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * When Fivecast generated the export the report was built from. Never the time
 * the report ran. Null for a report with no Fivecast export, or an export whose
 * name carries no stamp and has no upload time.
 */
export function threatReportExportStamp(
  report: Pick<CaseThreatReport, 'fivecastDataDirectory'>,
  uploadedAtByUrl: Record<string, string> = {},
): ThreatReportSourceStamp | null {
  return threatReportSourceStamp(report.fivecastDataDirectory ?? undefined, uploadedAtByUrl);
}

interface FormatOptions {
  locale?: string;
  /** IANA zone for a timed value. Defaults to the viewer's zone. */
  timeZone?: string;
}

/**
 * Format a date for the report header.
 *
 * A ``day`` stamp has no clock time, so it prints the UTC calendar date only.
 * Printing it in the viewer's zone could move it to the neighbouring day, and
 * a clock time would be invented.
 */
export function formatThreatReportDateTime(
  date: Date,
  precision: ThreatReportSourceStamp['precision'] = 'second',
  { locale, timeZone }: FormatOptions = {},
): string {
  if (precision === 'day') {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * Order a version family for the picker: newest Fivecast export first, then
 * newest run. A report with no known export time sorts after the dated ones.
 */
export function orderThreatReportVersions(
  versions: readonly CaseThreatReport[],
  uploadedAtByUrl: Record<string, string> = {},
): CaseThreatReport[] {
  const exportMillis = (report: CaseThreatReport): number =>
    threatReportExportStamp(report, uploadedAtByUrl)?.at.getTime() ?? Number.NEGATIVE_INFINITY;
  return [...versions].sort((a, b) => {
    const exportA = exportMillis(a);
    const exportB = exportMillis(b);
    if (exportA !== exportB) return exportB > exportA ? 1 : -1;
    return b.epochTimeSecs - a.epochTimeSecs;
  });
}
